<template>
    <div class="">
        <div class="flex bottom-tree-view-container items-center">
            <slot name="toolbarItems" />
            <dx-text-box
                v-model:value="textBoxValue"
                class="w-full search-bar"
                placeholder="Поиск..."
                mode="search"
                v-if="!props.meta.hideSearch"
                :value-change-event="props.meta.valueChangeEvent || 'keyup'"
                :hint="props.meta.searchHint || ''"
                @value-changed="onTextboxValueChanged"
            />
        </div>
        <dx-tree-view
            ref="treeViewRef"
            class="flex flex-col overflow-hidden bottom-tree-view"
            expanded-expr="isExpanded"
            :width="'100%'"
            :data-source="displayList"
            :key-expr="props.meta.keyExpr"
            :parent-id-expr="props.meta.parentIdExpr"
            :root-value="props.meta.rootValue"
            :data-structure="props.meta.dataStructure || 'plain'"
            :focus-state-enabled="true"
            :auto-navigate-to-focused-row="true"
            :select-by-click="props.meta.selectByClick"
            :expand-event="props.meta.expandEvent || 'dblclick'"
            :expanded-row-keys="props.meta.expandedRowKeys"
            :display-expr="props.meta.displayExpr"
            :selection-mode="props.meta.selectionMode || 'single'"
            :item-template="props.meta.itemTemplate"
            :use-native-scrolling="false"
            :scroll-direction="'both'"
            :animation-enabled="props.meta.animationEnabled ? props.meta.animationEnabled : true"
            :virtual-mode-enabled="props.meta.virtualModeEnabled || false"
            :height="props.meta.height ? props.meta.height : '100%'"
            :hasItemsExpr="meta.hasItemsExpr || 'hasItems'"
            @item-click="onItemClick"
            @item-rendered="onItemRendered"
            @item-expanded="onItemExpanding"
            @item-collapsed="onItemCollapsed"
            @item-selection-changed="onItemSelectionChanged"
            @content-ready="onContentReady"
        >
            <template v-for="(_, name) in $slots" #[name]="obj">
                <slot :name="name" :data="obj?.data" locate-in-menu="auto" />
            </template>
        </dx-tree-view>
    </div>
</template>
<script setup>
import { DxTreeView, DxTextBox } from 'devextreme-vue';
import { ref, onMounted, computed } from 'vue';

const treeViewRef = ref();
const treeViewInstance = ref();
const filterQuery = ref('');
const textBoxValue = ref('');
const timeOut = ref();
const filterMatches = ref(new Set());
const ds = ref();

const props = defineProps({
    dataSource: {
        type: Array,
        default: () => [],
    },
    meta: {
        type: Object,
        default: () => {},
    },
});

const displayList = props.meta.virtualModeEnabled
    ? props.dataSource
    : computed(() => filterTree(props.dataSource, filterQuery.value));

const emit = defineEmits([
    'itemClick',
    'emptyFilterResult',
    'filterResult',
    'itemExpanded',
    'itemCollapsed',
    'contentReady',
]);

onMounted(() => {
    treeViewInstance.value = treeViewRef.value.instance;
});

const onItemRendered = event => {
    if (filterMatches.value.has(event.itemData[props.meta.keyExpr]))
        event.itemElement.classList.add('active:bg-amber-400', 'focus:bg-amber-400');
};

const onTextboxValueChanged = async () => {
    treeViewRef.value.instance.selectedRowKeys = [];
    const { countToStartSearching, searchTimeOut } = props.meta;
    const charNum = countToStartSearching || 3;
    const ms = searchTimeOut || 2000;
    if (textBoxValue.value.length >= charNum) {
        if (timeOut.value) {
            clearTimeout(timeOut.value);
        }
        timeOut.value = setTimeout(() => {
            filterQuery.value = textBoxValue.value;
        }, ms);
    }
    if (textBoxValue.value === '') {
        filterQuery.value = textBoxValue.value;
        props.meta.expandAll ? treeViewInstance.value.expandAll() : treeViewInstance.value.collapseAll();
    }
};

const onContentReady = e => {
    emit('contentReady', e);
};

const onItemClick = event => {
    emit('itemClick', event);
};

const onItemExpanding = event => {
    emit('itemExpanded', event);
};

const onItemCollapsed = event => {
    emit('itemCollapsed', event);
};

const onItemSelectionChanged = event => {
    emit('itemSelectionChanged', event);
};

const filterTree = (dataSource, searchQuery) => {
    if (!searchQuery) {
        filterMatches.value = new Set();
        ds.value = dataSource;
        emit('filterResult', ds.value);
        return dataSource;
    }

    const { displayExpr, parentIdExpr, keyExpr } = props.meta;
    const data = [...dataSource];

    const idToNode = new Map();
    const childrenMap = new Map();
    const searchMatches = new Set();

    data.forEach(node => {
        node.isExpanded = false;
        idToNode.set(node[keyExpr], node);
        if (node[parentIdExpr] !== null) {
            if (!childrenMap.has(node[parentIdExpr])) {
                childrenMap.set(node[parentIdExpr], []);
            }
            childrenMap.get(node[parentIdExpr]).push(node[keyExpr]);
        }

        if (node[displayExpr]) {
            const currentParam = node[displayExpr].toLowerCase();
            if (currentParam.includes(searchQuery ? searchQuery.toLowerCase() : '')) searchMatches.add(node[keyExpr]);
        }
    });

    data.forEach(node => {
        if (searchMatches.has(node[keyExpr]) && node[parentIdExpr]) idToNode.get(node[parentIdExpr]).isExpanded = true;
    });

    const results = new Set();

    const addAncestors = nodeId => {
        const node = idToNode.get(nodeId);
        if (node) {
            if (node[parentIdExpr] && !results.has(node[parentIdExpr])) {
                results.add(node[parentIdExpr]);
                addAncestors(node[parentIdExpr]);
            }
        }
    };

    const addDescendants = nodeId => {
        if (childrenMap.has(nodeId)) {
            childrenMap.get(nodeId).forEach(childId => {
                if (!results.has(childId)) {
                    results.add(childId);
                    addDescendants(childId);
                }
            });
        }
    };

    searchMatches.forEach(nodeId => {
        results.add(nodeId);
        addAncestors(nodeId);
        addDescendants(nodeId);
    });

    filterMatches.value = searchMatches;
    const filteredData = data.filter(node => results.has(node[keyExpr]));

    ds.value = filteredData;
    ds.value.length === 0 ? emit('emptyFilterResult', []) : emit('filterResult', ds.value);
    return filteredData;
};

defineExpose({
    treeViewInstance,
    textBoxValue,
    ds,
});

//full meta object example
// const meta = {
//     valueChangeEvent: null, // "keyup" / "blur" / "change" / "input" / "focusout"
//     searchHint: null,
//     keyExpr: 'id',
//     parentIdExpr: 'parent_id',
//     rootValue: 'null',
//     dataStructure: null,
//     selectByClick: null,
//     expandEvent: null,
//     displayExpr: 'name',
//     selectionMode: null,
//     itemTemplate: null,
//     countToStartSearching: null,
//     searchTimeOut: null,
// };
</script>

<style lang="scss" scoped>
:deep(.dx-row-removed > td) {
    background-color: white;
    text-decoration: line-through;
}
</style>
